import { useState } from 'react';
import { AiOutlineUser } from 'react-icons/ai';
import { toastInfo, toastSuccess, toastError } from '../utils/toasts';
import MenuDropdown from './atoms/MenuDropdown';
import { useDeleteEventTransactionMutation } from '../store';
import { EventTransactionDTO, EVENT_TRANSACTION_TYPE_LABELS } from '../models/event-transaction';
import FilterTable, { BaseData } from './Table/Table';
import Button from './atoms/Button';
import { parseFriendlyDateAndHour } from '../utils/dates';
import { formatCurrency } from '../utils/shared';
import CustomModal from './atoms/CustomModal';
import AddUserExpense from './AddUserExpense';
import EmptyTable from './Table/EmptyTable';

interface Props {
  transactions: EventTransactionDTO[];
  businessId: string;
  eventId: string;
  role: string;
  buttonAction: () => void;
}

interface TableRows extends BaseData {
  id: string;
  date: string;
  description: string;
  amount: JSX.Element;
  name: string;
}

const TransactionsTable: React.FC<Props> = ({
  transactions, businessId, eventId, role, buttonAction,
}) => {
  const [deleteTransaction, resultsDelete] = useDeleteEventTransactionMutation();
  const [showModal, setShowModal] = useState(false);
  const [modalBody, setModalBody] = useState<React.ReactNode>(null);

  const handleDeleteTransaction = async (transaction: EventTransactionDTO) => {
    try {
      toastInfo('Eliminando...');
      await deleteTransaction({
        businessId,
        eventId,
        transactionId: transaction.id,
      }).unwrap()
        .then(() => {
          toastSuccess('Transacción eliminada con éxito');
        })
        .catch(() => {
          toastError('Error al eliminar transacción.');
        });
      setShowModal(false);
    } catch (error) {
      console.error('Error deleting transaction:', error);
    }
  };

  const handleDeleteConfirm = (transaction: EventTransactionDTO) => {
    setShowModal(true);
    setModalBody(
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-2'>
          <h1 className='text-lg font-medium'>Eliminar movimiento</h1>
          <p>¿Deseas eliminar este movimiento?</p>
          <p className='mt-2'>Una vez eliminado desaparecerá de la lista y no se podrá revertir.</p>
        </div>
        <div className='flex gap-3'>
          <Button
            onClick={() => setShowModal(false)}
            variant='secondary'
            disabled={resultsDelete.isLoading}
          >
            Cancelar
          </Button>
          <Button
            loading={resultsDelete.isLoading}
            onClick={() => handleDeleteTransaction(transaction)}
            disabled={resultsDelete.isLoading}
          >
            Eliminar
          </Button>
        </div>
      </div>,
    );
  };

  const handleEditTransaction = (transaction: EventTransactionDTO) => {
    setShowModal(true);
    setModalBody(
      <div className='flex flex-col gap-4'>
        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-medium">Editar movimiento</h1>
          <p className="text-text-tertiary text-sm">
            Podrás editar movimientos de este vendedor
          </p>
        </div>
        <AddUserExpense
          businessId={businessId}
          eventId={eventId}
          sellerId={transaction.user_id}
          onSuccess={() => {
            setShowModal(false);
          }}
          editTransaction
          transactionId={transaction.id}
          type={transaction.type}
        />
      </div>,
    );
  };

  const dropDownOptions = [
    ...(role === 'ADMIN' || role === 'ROOT' ? [{
      label: 'Editar',
      onClick: handleEditTransaction,
    }, {
      label: 'Eliminar',
      onClick: (data: EventTransactionDTO) => handleDeleteConfirm(data),
    },
    ] : []),
  ];

  const actions = (row: BaseData) => (
    <MenuDropdown<EventTransactionDTO>
      options={dropDownOptions}
      data={row as unknown as EventTransactionDTO}
      loading={resultsDelete.isLoading}
    />
  );

  const rows: TableRows[] = transactions.map((transaction) => ({
    id: transaction.id,
    date: parseFriendlyDateAndHour(transaction.created_at, false),
    description: transaction.description,
    amount: <div className='whitespace-nowrap'>{formatCurrency(transaction.amount)}</div>,
    type: <div className='whitespace-nowrap'>{EVENT_TRANSACTION_TYPE_LABELS[transaction.type]}</div>,
    name: transaction.user?.username,
  }));

  const config = [
    {
      id: 'date',
      label: 'Fecha',
    },
    {
      id: 'amount',
      label: 'Monto',
    },
    {
      id: 'description',
      label: 'Observación',
    },
    {
      id: 'type',
      label: 'Tipo',
    },
    {
      id: 'name',
      label: 'Creado por',
    },
  ];

  const customButtons = [
    ...(role === 'ADMIN' || role === 'ROOT' ? [
      <Button onClick={buttonAction} variant='secondary'>
        <div className='flex items-center gap-2 whitespace-nowrap'>
          <span>+</span>
          <span className='hidden sm:inline'>Registrar movimiento</span>
        </div>
      </Button>,
    ] : []),
  ];

  return (
    <div>
      {transactions.length > 0 ? (
        <FilterTable
          rows={rows}
          headCells={config}
          customButtons={customButtons}
          actions={role === 'ADMIN' || role === 'ROOT' ? actions : undefined}
          tableId="transactions-table"
        />
      ) : (
        <EmptyTable
          title="No hay movimientos hasta el momento"
          info="Por el momento no hay movimientos"
          icon={AiOutlineUser}
          iconColor="text-secondary-500"
          button={((role === 'ADMIN' || role === 'ROOT')) ? [
            {
              label: '+ \u200B \u200B Registrar movimiento',
              mobileLabel: ' + ',
              onClick: buttonAction,
              variant: 'secondary',
            },
          ] : undefined}
        />
      )}
      <CustomModal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        hideCancelButton
      >
        {modalBody}
      </CustomModal>
    </div>

  );
};

export default TransactionsTable;
